/** @jsx isolateComponent */

import { css } from '@emotion/core';

import { FlexLayout } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import {
  Animation,
  Colors,
  Icon,
  IconName,
  Margin,
  Shadows,
  Text,
} from '@zapier/design-system';

type TrialOptionButtonProps = {
  /** The name of the icon to show. */
  iconName: IconName;
  /** Shows the component in a selected state if true. */
  isSelected: boolean;
  /** The text to show below the icon. */
  label: string;
  /** The function called when the component is clicked. */
  onClick: () => void;
};

const Styles = {
  root: css`
    background: ${Colors.grey000};
    border: 1px solid transparent;
    border-radius: 10px;
    cursor: pointer;
    height: 100px;
    transition-property: background-color, border-color;
    transition-duration: ${Animation.transitionDuration};
    transition-timing-function: ${Animation.transitionTimingFunction};
    width: 150px;
    &:hover {
      border-color: ${Colors.grey200};
    }
  `,
  selected: css`
    background-color: ${Colors.white};
    border-color: ${Colors.grey200};
    box-shadow: ${Shadows.elevation5};
  `,
};

const TrialOptionButton = ({
  iconName,
  isSelected,
  label,
  onClick,
}: TrialOptionButtonProps) => (
  <button css={[Styles.root, isSelected && Styles.selected]} onClick={onClick}>
    <FlexLayout
      align="center"
      direction="column"
      gutter="none"
      justify="center"
    >
      {/* Without negative margin, the text is too far away from the icon */}
      <Margin margin="0 0 -10px 0">
        <Icon color="blue500" name={iconName} size={60} />
      </Margin>

      {/* Margin to make up for the negative margin above */}
      <Margin margin="0 0 10px 0">
        <Text color="blue500" tag="p" type="subtitle700">
          {label}
        </Text>
      </Margin>
    </FlexLayout>
  </button>
);

export { TrialOptionButton };
